import Vue from 'vue'

// The Platform's visual identity, per brand.
//
// This is the deliberate short version of white labelling (IND-2067): the
// palettes live in code, not in the database, and only the brand of the signed
// in customer is honoured. The real thing — brands administered in the
// backoffice, resolved from the hostname, covering email, SMS and push — is
// IND-2066, which replaces this file. Nothing here creates a table, an endpoint
// or a screen, so there is nothing to migrate later, only to delete.

export const BRAND_INOBRAM = 'inobram'
export const BRAND_AEI = 'aei'

const STORAGE_KEY = 'brand'

// Vuetify 1.5 recomputes `generatedStyles` from `$vuetify.theme` and rewrites
// the `vuetify-theme-stylesheet` element, so assigning to these keys reskins the
// running app. Only keys that already exist in the theme are reactive, which is
// why every brand below lists the same ones.
const THEMES = {
  [BRAND_INOBRAM]: {
    primary: '#1976D2',
    primaryWithoutLigth: '#113DBF',
    primary10: '#040f2f',
    primary20: '#081E5E',
    primary30: '#0D2D8C',
    primary60: '#446FEE',
    primary70: '#5E7CD4',
    primary90: '#D0DBFB',
    primary95: '#E8EDFD',
    secondaryWithoutLight: '#84BD13',
    secondary30: '#618b0e',
    tertiary: '#00aeef'
  },
  // Collected from automatedenvironments.com: the seal's blue (#2B89ED), the
  // navy behind it and the blue of the site's own theme (#0088CB). The reds and
  // yellows of that palette are deliberately unused — AEI's red sits a shade
  // away from this app's error red, and a brand colour that reads as a state
  // would be worse than no brand at all.
  [BRAND_AEI]: {
    primary: '#1E6FC4',
    primaryWithoutLigth: '#1E6FC4',
    primary10: '#0C1826',
    primary20: '#152B42',
    primary30: '#1E3650',
    primary60: '#2B89ED',
    primary70: '#5CA6F2',
    primary90: '#CFE4FB',
    primary95: '#E8F2FE',
    secondaryWithoutLight: '#0088CB',
    secondary30: '#006C9F',
    tertiary: '#0088CB'
  }
}

const BRANDS = {
  [BRAND_INOBRAM]: {
    name: BRAND_INOBRAM,
    title: 'Plataforma Inobram',
    logo: '/static/img/inobram-logo-branco.png',
    logoCompact: '/static/img/inobram-logo-branco.svg',
    favicon: '/static/img/favicon.png',
    navInk: 'primary70',
    seal: null,
    // Inobram's demo environment and its Brazilian support line: a partner's
    // customer must not be sent to either.
    support: {
      demoUrl: 'https://demo.plataforma.inobram.com.br',
      whatsappUrl: 'https://wa.me/5541999960305',
      phone: '(41) 9 9996-0305'
    }
  },
  [BRAND_AEI]: {
    name: BRAND_AEI,
    title: 'AEI Integra',
    // Both places that show a logo are dark, and AEI's logo is a single colour,
    // so the white variants are the brand's own artwork with its fill changed.
    // The full lockup carries the "by Speria" line, which is four pixels tall at
    // the toolbar's 42px — hence the monogram alone there.
    logo: '/static/img/aei-logo-branco.svg',
    logoCompact: '/static/img/aei-mark-branco.svg',
    favicon: '/static/img/aei-favicon.png',
    navInk: 'primary30',
    // AEI's "Total Poultry Control" badge. It carries dark artwork, so it only
    // works on a light surface: the sign-in form's card, not the page behind it.
    seal: '/static/img/aei-seal.png',
    // AEI supports its own customers, and has no demo of this Platform to show
    // them. Until a channel of theirs is agreed, the sign-in screen offers none.
    support: null
  }
}

function readRememberedBrand () {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return BRANDS[stored] ? stored : BRAND_INOBRAM
  } catch (err) {
    return BRAND_INOBRAM
  }
}

// The sign-in screen is drawn before anyone is signed in, so the brand of the
// customer cannot be known there. Remembering the last one keeps a returning
// customer's own login screen branded; a first visit, or a shared browser,
// falls back to Inobram and corrects itself on the next screen.
const state = Vue.observable({ name: readRememberedBrand() })

export function currentBrandName () {
  return state.name
}

export function brand () {
  return BRANDS[state.name] || BRANDS[BRAND_INOBRAM]
}

/** The brand's theme tokens, for colours painted from JavaScript. */
export function palette () {
  return THEMES[state.name] || THEMES[BRAND_INOBRAM]
}

export function applyBrand (vuetify, name) {
  const chosen = BRANDS[name] ? name : BRAND_INOBRAM
  state.name = chosen

  const theme = THEMES[chosen]
  if (vuetify && vuetify.theme) {
    Object.keys(theme).forEach(token => { vuetify.theme[token] = theme[token] })
  }

  if (typeof document !== 'undefined') {
    // For the colours that live in component styles rather than in the theme.
    const root = document.documentElement
    root.setAttribute('data-brand', chosen)
    root.style.setProperty('--brand-primary', theme.primary60)
    root.style.setProperty('--brand-primary-strong', theme.primaryWithoutLigth)
    root.style.setProperty('--brand-primary-dark', theme.primary30)
    root.style.setProperty('--brand-surface', theme.primary95)
    root.style.setProperty('--brand-nav-ink', theme[BRANDS[chosen].navInk])
    root.style.setProperty('--brand-accent', theme.secondaryWithoutLight)

    document.title = BRANDS[chosen].title
    const favicon = document.querySelector('link[rel="shortcut icon"], link[rel="icon"]')
    if (favicon) favicon.href = BRANDS[chosen].favicon
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, chosen)
  } catch (err) {
    // A browser that refuses storage simply starts from Inobram every time.
  }
}



// WEBPACK FOOTER //
// ./src/utils/brand.js